export interface LatLng {
  lat: number;
  lng: number;
}

export interface GeoBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface LandPolygon {
  outer: LatLng[];
  holes: LatLng[][];
  bbox: GeoBounds;
}

interface Topology {
  transform?: { scale: [number, number]; translate: [number, number] };
  arcs: number[][][];
  objects: {
    land: {
      geometries: Array<{
        type: string;
        arcs: number[][][] | number[][][][];
      }>;
    };
  };
}

let cachedLand: LandPolygon[] | null = null;
let loadPromise: Promise<LandPolygon[]> | null = null;

function rayCast(point: LatLng, polygon: LatLng[]): boolean {
  if (!polygon || polygon.length < 3) return false;
  const { lat, lng } = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    const intersect = ((yi > lat) !== (yj > lat))
      && (lng < ((xj - xi) * (lat - yi)) / (yj - yi + Number.EPSILON) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function ringBBox(ring: LatLng[]): GeoBounds {
  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;
  for (const p of ring) {
    if (p.lat > north) north = p.lat;
    if (p.lat < south) south = p.lat;
    if (p.lng > east) east = p.lng;
    if (p.lng < west) west = p.lng;
  }
  return { north, south, east, west };
}

function decodeArc(arc: number[][], transform?: Topology['transform']): LatLng[] {
  const scale = transform?.scale ?? [1, 1];
  const translate = transform?.translate ?? [0, 0];
  let x = 0;
  let y = 0;
  return arc.map(([dx, dy]) => {
    x += dx;
    y += dy;
    return { lng: x * scale[0] + translate[0], lat: y * scale[1] + translate[1] };
  });
}

function stitchRing(arcIdxs: number[], decoded: LatLng[][]): LatLng[] {
  const ring: LatLng[] = [];
  for (const idx of arcIdxs) {
    const reverse = idx < 0;
    const arc = decoded[reverse ? ~idx : idx];
    const seq = reverse ? [...arc].reverse() : arc;
    if (ring.length) ring.push(...seq.slice(1));
    else ring.push(...seq);
  }
  return ring;
}

function wrapLng(lng: number): number {
  let x = lng;
  while (x > 180) x -= 360;
  while (x < -180) x += 360;
  return x;
}

function isWrapSliver(ring: LatLng[]): boolean {
  if (ring.length < 3) return true;
  const b = ringBBox(ring);
  const lngSpan = b.east - b.west;
  const latSpan = b.north - b.south;
  if (lngSpan > 300 && latSpan < 30) return true;
  if (ring.length <= 8 && lngSpan > 300 && latSpan > 90) return true;
  return false;
}

function splitRingForLeaflet(ring: LatLng[]): LatLng[][] {
  if (ring.length < 3) return [];
  const closed = Math.abs(ring[0].lat - ring[ring.length - 1].lat) < 1e-9
    && Math.abs(ring[0].lng - ring[ring.length - 1].lng) < 1e-9;
  const pts = (closed ? ring.slice(0, -1) : ring).map((p) => ({ lat: p.lat, lng: wrapLng(p.lng) }));
  const parts: LatLng[][] = [];
  let current: LatLng[] = [];
  for (const p of pts) {
    const prev = current[current.length - 1];
    if (prev && Math.abs(p.lng - prev.lng) > 180) {
      const lat = (prev.lat + p.lat) / 2;
      current.push({ lat, lng: prev.lng > 0 ? 179.999 : -179.999 });
      if (current.length >= 3 && !isWrapSliver(current)) parts.push(current);
      current = [{ lat, lng: p.lng > 0 ? 179.999 : -179.999 }, p];
    } else {
      current.push(p);
    }
  }
  if (current.length >= 3 && !isWrapSliver(current)) parts.push(current);
  return parts;
}

function sanitizeLandPolygons(features: LandPolygon[]): LandPolygon[] {
  const out: LandPolygon[] = [];
  for (const feat of features) {
    const outers = splitRingForLeaflet(feat.outer);
    const holeParts = feat.holes.flatMap((h) => splitRingForLeaflet(h));
    for (const outer of outers) {
      const bbox = ringBBox(outer);
      const holes = holeParts.filter((h) => rayCast(h[Math.floor(h.length / 3)] || h[0], outer));
      out.push({ outer, holes, bbox });
    }
  }
  return out;
}

function decodeTopology(topo: Topology): LandPolygon[] {
  const decoded = topo.arcs.map((arc) => decodeArc(arc, topo.transform));
  const features: LandPolygon[] = [];
  for (const geom of topo.objects.land.geometries) {
    if (geom.type !== 'MultiPolygon' && geom.type !== 'Polygon') continue;
    const polygons = geom.type === 'Polygon'
      ? [geom.arcs as number[][][]]
      : (geom.arcs as number[][][][]);
    for (const poly of polygons) {
      const outer = stitchRing(poly[0], decoded);
      if (outer.length < 3) continue;
      const holes = poly.slice(1).map((h) => stitchRing(h, decoded)).filter((h) => h.length >= 3);
      features.push({ outer, holes, bbox: ringBBox(outer) });
    }
  }
  return sanitizeLandPolygons(features);
}

export async function loadWorldLand(): Promise<LandPolygon[]> {
  if (cachedLand) return cachedLand;
  if (!loadPromise) {
    loadPromise = fetch('/geo/land-110m.json')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load land map');
        return res.json() as Promise<Topology>;
      })
      .then((topo) => {
        cachedLand = decodeTopology(topo);
        return cachedLand;
      })
      .catch((err) => {
        loadPromise = null;
        throw err;
      });
  }
  return loadPromise;
}

export function isGlobeLikeBounds(bounds: GeoBounds): boolean {
  return (bounds.north - bounds.south) > 100 && (bounds.east - bounds.west) > 250;
}

export function isGlobeLikePolygon(polygon: LatLng[]): boolean {
  if (!polygon.length) return false;
  return isGlobeLikeBounds(ringBBox(polygon));
}

export function boundsFromPolygon(polygon: LatLng[]): GeoBounds | null {
  if (!polygon.length) return null;
  return ringBBox(polygon);
}

function lerpLngLat(a: LatLng, b: LatLng, t: number): LatLng {
  return { lat: a.lat + t * (b.lat - a.lat), lng: a.lng + t * (b.lng - a.lng) };
}

function clipRingToBounds(ring: LatLng[], bounds: GeoBounds): LatLng[] {
  if (ring.length < 3) return [];
  const closed = ring[0].lat === ring[ring.length - 1].lat && ring[0].lng === ring[ring.length - 1].lng;
  let output = closed ? ring.slice(0, -1) : ring.slice();

  const edges: Array<{ inside: (p: LatLng) => boolean; intersect: (a: LatLng, b: LatLng) => LatLng }> = [
    {
      inside: (p) => p.lng >= bounds.west,
      intersect: (a, b) => lerpLngLat(a, b, (bounds.west - a.lng) / ((b.lng - a.lng) || 1e-12)),
    },
    {
      inside: (p) => p.lng <= bounds.east,
      intersect: (a, b) => lerpLngLat(a, b, (bounds.east - a.lng) / ((b.lng - a.lng) || 1e-12)),
    },
    {
      inside: (p) => p.lat >= bounds.south,
      intersect: (a, b) => lerpLngLat(a, b, (bounds.south - a.lat) / ((b.lat - a.lat) || 1e-12)),
    },
    {
      inside: (p) => p.lat <= bounds.north,
      intersect: (a, b) => lerpLngLat(a, b, (bounds.north - a.lat) / ((b.lat - a.lat) || 1e-12)),
    },
  ];

  for (const edge of edges) {
    if (output.length < 3) return [];
    const input = output;
    output = [];
    for (let i = 0; i < input.length; i += 1) {
      const cur = input[i];
      const prev = input[(i + input.length - 1) % input.length];
      const curIn = edge.inside(cur);
      const prevIn = edge.inside(prev);
      if (curIn) {
        if (!prevIn) output.push(edge.intersect(prev, cur));
        output.push(cur);
      } else if (prevIn) {
        output.push(edge.intersect(prev, cur));
      }
    }
  }
  return output.length >= 3 ? output : [];
}

function bboxesOverlap(a: GeoBounds, b: GeoBounds): boolean {
  return a.west <= b.east && a.east >= b.west && a.south <= b.north && a.north >= b.south;
}

export function clipLandToBounds(bounds: GeoBounds, land = cachedLand): LandPolygon[] {
  if (!land) return [];
  if (isGlobeLikeBounds(bounds)) return land;
  const clipped: LandPolygon[] = [];
  for (const feat of land) {
    if (!bboxesOverlap(feat.bbox, bounds)) continue;
    const outer = clipRingToBounds(feat.outer, bounds);
    if (outer.length < 3) continue;
    const holes = feat.holes
      .map((hole) => clipRingToBounds(hole, bounds))
      .filter((hole) => hole.length >= 3);
    clipped.push({ outer, holes, bbox: ringBBox(outer) });
  }
  return clipped;
}

export function landRingsForLeaflet(parts: LandPolygon[]): [number, number][][][] {
  const close = (ring: LatLng[]): [number, number][] => {
    const pts: [number, number][] = [];
    for (const p of ring) {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue;
      if (Math.abs(p.lat) > 90 || Math.abs(p.lng) > 180) continue;
      pts.push([p.lat, p.lng]);
    }
    if (pts.length < 3) return [];
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) pts.push(first);
    return pts;
  };
  return parts
    .map((part) => close(part.outer))
    .filter((ring) => ring.length >= 4)
    .map((ring) => [ring]);
}

/** Shoelace in lng/lat — positive ⇒ counter-clockwise (Google Maps outer-ring convention). */
export function signedRingArea(ring: LatLng[]): number {
  if (ring.length < 3) return 0;
  let area = 0;
  const n = ring.length;
  const closed = Math.abs(ring[0].lat - ring[n - 1].lat) < 1e-12
    && Math.abs(ring[0].lng - ring[n - 1].lng) < 1e-12;
  const len = closed ? n - 1 : n;
  for (let i = 0; i < len; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % len];
    area += a.lng * b.lat - b.lng * a.lat;
  }
  return area / 2;
}

/** Ensure outer ring is CCW so Google fills land interior, not oceans. */
export function ensureGoogleOuterRing(ring: LatLng[]): LatLng[] {
  if (ring.length < 3) return ring;
  if (signedRingArea(ring) >= 0) return ring;
  return ring.slice().reverse();
}

export function landRingsForGoogle(parts: LandPolygon[]): LatLng[][] {
  return landRingsForLeaflet(parts)
    .map((poly) => (poly[0] || []).map(([lat, lng]) => ({ lat, lng })))
    .filter((ring) => ring.length >= 3)
    .map(ensureGoogleOuterRing);
}
